import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Calendar as CalIcon, Users, Plane, MapPin, Clock, ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import { fmtChicagoMonthYear, fmtChicagoLongDate } from "@/utils/timezone";

export function CalendarTab({ bookings, onViewBooking }) {
  const [selectedDate, setSelectedDate] = useState(new Date());

  // Group bookings by date
  const bookingsByDate = useMemo(() => {
    const map = {};
    (bookings || []).forEach(b => {
      if (!b.travel_date || b.booking_status === "cancelled") return;
      const d = b.travel_date;
      if (!map[d]) map[d] = [];
      map[d].push(b);
    });
    return map;
  }, [bookings]);

  // Dates that have bookings (for calendar highlighting)
  const bookedDates = useMemo(() => {
    return Object.keys(bookingsByDate).map(d => new Date(d + "T12:00:00"));
  }, [bookingsByDate]);

  // Format selected date to match booking travel_date format
  const selectedDateStr = selectedDate.toISOString().split("T")[0];
  const dayBookings = bookingsByDate[selectedDateStr] || [];
  const totalPax = dayBookings.reduce((sum, b) => sum + (b.num_passengers || 1), 0);

  // Get month summary
  const monthStart = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
  const monthEnd = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0);
  const monthBookings = (bookings || []).filter(b => {
    if (!b.travel_date || b.booking_status === "cancelled") return false;
    const d = new Date(b.travel_date + "T12:00:00");
    return d >= monthStart && d <= monthEnd;
  });
  const monthPax = monthBookings.reduce((sum, b) => sum + (b.num_passengers || 1), 0);
  const monthDays = [...new Set(monthBookings.map(b => b.travel_date))].length;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="calendar-tab">
      {/* Calendar */}
      <Card className="border-0 shadow-lg rounded-2xl lg:col-span-1">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <CalIcon className="w-4 h-4 text-green-600" />
            Trip Calendar
          </CardTitle>
        </CardHeader>
        <CardContent className="p-2 flex justify-center">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={(d) => d && setSelectedDate(d)}
            modifiers={{ booked: bookedDates }}
            modifiersStyles={{
              booked: {
                backgroundColor: "#dcfce7",
                color: "#166534",
                fontWeight: "bold",
                borderRadius: "8px"
              }
            }}
            className="rounded-xl"
          />
        </CardContent>
        {/* Month Summary */}
        <CardContent className="pt-0 pb-4 px-4">
          <div className="bg-green-50 rounded-xl p-3 space-y-1">
            <p className="text-xs font-bold text-green-800">
              {fmtChicagoMonthYear(monthStart)}
            </p>
            <div className="flex justify-between text-xs">
              <span className="text-green-700">{monthBookings.length} bookings</span>
              <span className="text-green-700">{monthPax} passengers</span>
              <span className="text-green-700">{monthDays} days</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Day View */}
      <Card className="border-0 shadow-lg rounded-2xl lg:col-span-2">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <CalIcon className="w-4 h-4" />
              {fmtChicagoLongDate(selectedDate)}
            </CardTitle>
            {dayBookings.length > 0 && (
              <Badge className="bg-green-100 text-green-800">
                {dayBookings.length} booking{dayBookings.length > 1 ? 's' : ''} | {totalPax} pax
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="max-h-[500px] overflow-y-auto">
          {dayBookings.length === 0 ? (
            <div className="text-center py-12">
              <CalIcon className="w-10 h-10 mx-auto text-muted-foreground/20 mb-3" />
              <p className="text-muted-foreground">No trips scheduled for this day</p>
              <p className="text-xs text-muted-foreground mt-1">Select a highlighted date to see bookings</p>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Group by airport */}
              {Object.entries(
                dayBookings.reduce((groups, b) => {
                  const airport = b.pickup_location || "Unknown";
                  if (!groups[airport]) groups[airport] = [];
                  groups[airport].push(b);
                  return groups;
                }, {})
              ).map(([airport, airportBookings]) => (
                <div key={airport} className="border rounded-xl overflow-hidden">
                  <div className="bg-gray-50 px-3 py-2 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-3.5 h-3.5 text-blue-600" />
                      <span className="font-semibold text-sm">{airport}</span>
                    </div>
                    <Badge variant="secondary" className="text-[10px]">
                      {airportBookings.reduce((s, b) => s + (b.num_passengers || 1), 0)} pax
                    </Badge>
                  </div>
                  <div className="divide-y">
                    {airportBookings.sort((a, b) => (a.travel_time || "").localeCompare(b.travel_time || "")).map((booking) => (
                      <div
                        key={booking.id}
                        className="px-3 py-2.5 hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={() => onViewBooking?.(booking)}
                        data-testid={`cal-booking-${booking.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 min-w-0">
                            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                              booking.booking_status === "confirmed" ? "bg-green-500" :
                              booking.booking_status === "pending" ? "bg-yellow-500" : "bg-gray-400"
                            }`} />
                            <span className="font-medium text-sm truncate">{booking.full_name}</span>
                            <Badge variant="secondary" className="text-[9px] px-1 py-0 flex-shrink-0">
                              {booking.num_passengers || 1} pax
                            </Badge>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {booking.travel_time && (
                              <span className="text-xs text-muted-foreground flex items-center gap-1">
                                <Clock className="w-3 h-3" /> {booking.travel_time}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3 mt-1 pl-4">
                          {booking.flight_number && (
                            <span className="text-xs text-blue-600 flex items-center gap-1">
                              <Plane className="w-3 h-3" /> {booking.flight_number}
                            </span>
                          )}
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <ArrowRight className="w-3 h-3" />
                            {booking.dropoff_location || booking.housing_unit || "TBD"}
                          </span>
                          {booking.trip_type === "return" && (
                            <Badge className="text-[9px] px-1 py-0 bg-orange-100 text-orange-700">Return</Badge>
                          )}
                          <Badge className={`text-[9px] px-1 py-0 ${
                            booking.payment_status === "paid" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"
                          }`}>{booking.payment_status}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
